import type { WaterDataRow } from '../../utils/waterTable'
import { findAvailableLotMatch } from '../../utils/lotIdentity'

type AvailableLot = {
  id: string
  lotKey: string
  stage?: string
}

type WaterRowLotLike = {
  id?: unknown
  lot?: unknown
  lotId?: unknown
  unmappedLot?: unknown
}

const buildLotKeyById = (lots: ReadonlyArray<AvailableLot>): Map<string, string> =>
  new Map(lots.map((lot) => [String(lot.id), String(lot.lotKey ?? '')] as const))

const hydrateLoadedWaterRowLot = (
  row: WaterDataRow,
  availableLots: AvailableLot[],
  lotKeyById: Map<string, string>,
  farmLots: AvailableLot[],
  farmLotKeyById: Map<string, string>
): WaterDataRow => {
  const explicitLotId = String((row as any).lotId ?? '').trim()
  const explicitLotLabel = String((row as any).lot ?? '').trim()
  const unmappedLotLabel = String((row as any).unmappedLot ?? '').trim()

  if (explicitLotId) {
    const mappedLotLabel = lotKeyById.get(explicitLotId)
    if (mappedLotLabel) {
      if (mappedLotLabel === explicitLotLabel && !unmappedLotLabel) {
        return row
      }

      return {
        ...row,
        lotId: explicitLotId,
        lot: mappedLotLabel,
        unmappedLot: ''
      }
    }

    const resolvedLotFromId = findAvailableLotMatch(explicitLotId, availableLots)
    if (resolvedLotFromId.status === 'matched') {
      return {
        ...row,
        lotId: resolvedLotFromId.match.id,
        lot: lotKeyById.get(resolvedLotFromId.match.id) ?? resolvedLotFromId.match.lotKey,
        unmappedLot: ''
      }
    }

    // Track A finding 4.2 (Water parity): a lotId not in this production unit's
    // roster but real elsewhere on the farm belongs to a lot that was
    // transferred out. Keep the reading labelled with that lot — its history
    // here is real. The Water lot cell renders it read-only ("transferido").
    const farmLabel = farmLotKeyById.get(explicitLotId)
    if (farmLabel !== undefined) {
      const nextLabel = farmLabel || explicitLotLabel
      if (nextLabel === explicitLotLabel && !unmappedLotLabel) return row
      return {
        ...row,
        lotId: explicitLotId,
        lot: nextLabel,
        unmappedLot: ''
      }
    }

    // Findings #133: the reading names a lot that is no longer in this farm's
    // list (deleted, or moved to another farm). Its lotId is the identity —
    // never re-point it by label to a current lot that happens to share the
    // name (e.g. a deleted "Lote 1" and a new "Lote 1").
    if (farmLots.length > 0 && explicitLotLabel) return row
  }

  const resolvedLotLabel = explicitLotLabel || unmappedLotLabel
  if (!resolvedLotLabel) return row

  const resolvedLotFromLabel = findAvailableLotMatch(resolvedLotLabel, availableLots)
  if (resolvedLotFromLabel.status === 'matched') {
    return {
      ...row,
      lotId: resolvedLotFromLabel.match.id,
      lot: lotKeyById.get(resolvedLotFromLabel.match.id) ?? resolvedLotFromLabel.match.lotKey,
      unmappedLot: ''
    }
  }

  const resolvedFarmLotFromLabel = findAvailableLotMatch(resolvedLotLabel, farmLots)
  if (resolvedFarmLotFromLabel.status === 'matched') {
    return {
      ...row,
      lotId: resolvedFarmLotFromLabel.match.id,
      lot: farmLotKeyById.get(resolvedFarmLotFromLabel.match.id) ?? resolvedFarmLotFromLabel.match.lotKey,
      unmappedLot: ''
    }
  }

  return row
}

export const hydrateLoadedWaterRows = (
  rows: WaterDataRow[],
  availableLots: AvailableLot[],
  farmLots: AvailableLot[] = []
): WaterDataRow[] => {
  if (rows.length === 0) return rows
  if (availableLots.length === 0 && farmLots.length === 0) return rows

  const lotKeyById = buildLotKeyById(availableLots)
  const farmLotKeyById = buildLotKeyById(farmLots)
  let changed = false

  const nextRows = rows.map((row) => {
    const hydratedRow = hydrateLoadedWaterRowLot(row, availableLots, lotKeyById, farmLots, farmLotKeyById)
    if (hydratedRow !== row) changed = true
    return hydratedRow
  })

  return changed ? nextRows : rows
}

/**
 * Row ids whose lot lives in a DIFFERENT production unit now (transferred out
 * after producing readings here). The Water lot cell shows these read-only with
 * a "transferido" tag instead of a lot dropdown stuck on "(ninguno)".
 * Track A finding 4.2 (Water parity).
 */
export const getForeignWaterLotIds = (
  rows: ReadonlyArray<WaterRowLotLike>,
  availableLots: AvailableLot[],
  farmLots: AvailableLot[]
): Set<string> => {
  const out = new Set<string>()
  if (rows.length === 0 || farmLots.length === 0) return out

  const puIds = new Set(availableLots.map((lot) => String(lot.id)))
  const farmIds = new Set(farmLots.map((lot) => String(lot.id)))

  for (const row of rows) {
    const rowId = String(row.id ?? '').trim()
    if (!rowId) continue

    const lotId = String(row.lotId ?? '').trim()
    if (lotId) {
      if (!puIds.has(lotId) && farmIds.has(lotId)) out.add(rowId)
      continue
    }

    const label = String(row.lot ?? '').trim() || String(row.unmappedLot ?? '').trim()
    if (!label) continue
    const inPu = availableLots.length > 0 && findAvailableLotMatch(label, availableLots).status === 'matched'
    const inFarm = findAvailableLotMatch(label, farmLots).status === 'matched'
    if (!inPu && inFarm) out.add(rowId)
  }

  return out
}
